import React from 'react';

import amplitude from 'amplitude-js';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import ServerErrorPopup from 'apps/auth/components/utils/ServerErrorPopup';
import { ACCOUNT_CHANGED, GET_CHAT_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { CARD_TYPE, FROM, TO } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { cardType } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { AUTH_GA_ACCOUNTS, AUTH_GA_CARD, AUTH_GA_POCKET, AUTH_GA_STUB_PAGE, AUTH_GA_VIRTUAL_CARD_CREATE_PREVIEW } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { resolveArguments } from 'apps/auth/helpers/utils';

type AmplitudeInstance = {
    logEvent: (event: string, payload?: Record<string, unknown>) => void;
};
const amplitudeInstance = amplitude as { getInstance: () => AmplitudeInstance };

type PopupRef = React.RefObject<{ open: (content: React.ReactNode) => void; close: () => void }>;

type Actions = {
    toggleSpinner: (value: boolean) => Promise<unknown> | void;
    updatePageMessages: (messages: Record<string, string>) => Promise<unknown> | void;
    addExtras: (extra: Record<string, unknown>) => Promise<unknown>;
    toggleModalWithTimer: (value: boolean, content?: React.ReactNode | string, callback?: (() => void) | false) => Promise<unknown> | void;
    popupRef?: PopupRef;
    toggleModal: (value: boolean, content?: React.ReactNode) => Promise<unknown> | void;
};

type CardCurrency = { ncode?: string; code?: string };
type CardPocket = { currency: CardCurrency };
type Card = {
    id?: string | number;
    frozenByUser?: boolean;
    frozenBySystem?: boolean;
    expired?: boolean;
    plastic?: boolean;
    virtual?: boolean;
    panGroupLast4Digits?: string;
    cardPocket: CardPocket;
};

type Account = {
    id?: string | number;
    pocketCurrency: { code?: string };
};

type Messages = Record<string, string>;

type IbanResponse = {
    messages?: Record<string, string>;
    extra?: {
        account?: { bic?: string };
        [key: string]: unknown;
    };
};

const closeModal = (isTopUp?: boolean, id?: string | number) => {
    if (isTopUp) {
        return () => {
            window.location.href = `${AUTH_GA_POCKET}${id}/top-up/bank-transfer`;
        };
    }
};

export const requestForReceivingIban = (
    id: string | number,
    actions: Actions,
    Component: React.ReactNode,
    isTopUp: boolean,
    accountDetailsIssued: React.ReactNode | string,
): void => {
    void actions.toggleSpinner(true);
    void apiFetchData<IbanResponse>(`${AUTH_GA_POCKET}${id}/get/iban`)
        .then((json: IbanResponse) => {
            if (json.messages) {
                void actions.updatePageMessages(json.messages);
            }
            if (json.extra && Object.keys(json.extra).length > 0) {
                void actions.addExtras(json.extra).then(() => {
                    if (json.extra?.account?.bic) {
                        void actions.toggleModalWithTimer(true, accountDetailsIssued, closeModal(isTopUp, id));
                    } else if (json.messages?.somethingWentWrong) {
                        actions.popupRef?.current?.open(
                            <ServerErrorPopup
                                popupRef={actions.popupRef}
                                errorText={json.messages.somethingWentWrong}
                                buttonClose={json.messages.buttonClose}
                            />,
                        );
                    } else {
                        void actions.toggleModal(true, Component);
                    }
                });
            }
        })
        .finally(() => {
            void actions.toggleSpinner(false);
        })
        .catch((error) => {
            logWarn(error);
        });
};

const clickOnReissueCard = (
    card: Card,
    popupRef: PopupRef | undefined,
    toggleSpinner: (value: boolean) => Promise<unknown> | void,
    e?: React.MouseEvent,
): void => {
    e?.preventDefault();
    popupRef?.current?.close();
    if (card?.plastic) {
        window.location.href = AUTH_GA_STUB_PAGE + '?linkForNotNow=' + AUTH_GA_ACCOUNTS;
        return;
    }
    void toggleSpinner(true);
    void apiFetchData(`/auth/ga/card/${card?.id}/remove`)
        .then(() => {
            window.location.href = AUTH_GA_VIRTUAL_CARD_CREATE_PREVIEW;
        })
        .catch((error) => logWarn(error))
        .finally(() => {
            void toggleSpinner(false);
        });
};

export const curryRequestsForLinkingCard = (
    toggleSpinner: (value: boolean) => Promise<unknown>,
    addExtras: (extra: Record<string, unknown>) => Promise<unknown>,
    toggleModalWithTimer: (value: boolean, content?: React.ReactNode | string, callback?: (() => void) | false) => Promise<unknown>,
    popupRef: PopupRef | undefined,
    toggleChat: (value: boolean) => void,
    toggleModal: (value: boolean, content?: React.ReactNode) => void,
    messages: Messages,
) => {
    return (card: Card, account: Account, messageRelinked: string, messageUnfreeze: string) => {
        const { id, frozenByUser = false, frozenBySystem = false, expired } = card;
        const onClickHandlerUnfreezeCardByUser = (e: React.MouseEvent) => {
            e.preventDefault();
            popupRef?.current?.close();
            void toggleSpinner(true);
            void apiFetchData<{ extra?: Record<string, unknown> }>(`${AUTH_GA_CARD}${id}/unfreeze`)
                .then((json) => {
                    if (json?.extra) {
                        void addExtras(json.extra);
                        return apiFetchData<{ extra?: Record<string, unknown> }>(`${AUTH_GA_POCKET}${account.id}/link/to-card/${id}`);
                    } else {
                        void toggleSpinner(false);
                    }
                })
                .then((res) => {
                    if (res?.extra) {
                        amplitudeInstance.getInstance().logEvent(ACCOUNT_CHANGED, {
                            [CARD_TYPE]: card.virtual ? cardType.VIRTUAL : cardType.PLASTIC,
                            [FROM]: card.cardPocket.currency.ncode,
                            [TO]: account.pocketCurrency.code,
                        });
                        void addExtras(res.extra).then(() => {
                            void toggleSpinner(false).then(() => {
                                void toggleModalWithTimer(true, messages.messageCardUnfrozen, () => {
                                    void toggleModalWithTimer(false, '', false).then(() => toggleModalWithTimer(true, messageRelinked));
                                });
                            });
                        });
                    } else {
                        void toggleSpinner(false);
                    }
                })
                .catch((error) => {
                    void toggleSpinner(false);
                    logWarn(error);
                });
        };

        if (frozenBySystem) {
            popupRef?.current?.open(
                <SimplePopup popupRef={popupRef} text={<div dangerouslySetInnerHTML={{ __html: messages.frozenBySystemInfoError }} />}>
                    <PopupButton
                        iconName="message-bubble"
                        text={messages.morePopupChatTitle}
                        onClick={(e) => {
                            e.preventDefault();
                            amplitudeInstance.getInstance().logEvent(GET_CHAT_CLICKED);
                            popupRef?.current?.close();
                            toggleChat(true);
                        }}
                    />
                </SimplePopup>,
            );
        } else if (frozenByUser) {
            popupRef?.current?.open(
                <SimplePopup
                    popupRef={popupRef}
                    text={
                        <>
                            <p>{messageUnfreeze}</p>
                            <p>{messages.linkFrozenCard}</p>
                        </>
                    }
                >
                    <PopupButton iconName="snowflake-crossed" text={messages.unfreezeCard} onClick={onClickHandlerUnfreezeCardByUser} />
                </SimplePopup>,
            );
        } else if (expired) {
            popupRef?.current?.open(
                <SimplePopup
                    popupRef={popupRef}
                    text={
                        <>
                            <p>
                                {resolveArguments(
                                    card.plastic ? messages.linkExpiredPlasticCardText1 : messages.linkExpiredVirtualCardText1,
                                    card.panGroupLast4Digits,
                                )}
                            </p>
                            <p>{card.plastic ? '' : messages.linkExpiredVirtualCardText2}</p>
                        </>
                    }
                >
                    <PopupButton
                        iconName={card.plastic ? 'check' : 'arrow-exchange'}
                        text={card.plastic ? messages.gotIt : messages.reopenExpiredCardButton}
                        onClick={(e: React.MouseEvent) => {
                            if (card.plastic) {
                                e.preventDefault();
                                popupRef?.current?.close();
                            } else {
                                clickOnReissueCard(card, popupRef, toggleSpinner, e);
                            }
                        }}
                    />
                </SimplePopup>,
            );
        } else {
            void toggleSpinner(true);
            void apiFetchData<{ extra?: Record<string, unknown> }>(`${AUTH_GA_POCKET}${account.id}/link/to-card/${id}`)
                .then((json) => {
                    if (json?.extra) {
                        amplitudeInstance.getInstance().logEvent(ACCOUNT_CHANGED, {
                            [CARD_TYPE]: card.virtual ? cardType.VIRTUAL : cardType.PLASTIC,
                            [FROM]: card.cardPocket.currency.code,
                            [TO]: account.pocketCurrency.code,
                        });
                        void addExtras(json.extra).then(() => toggleModalWithTimer(true, messageRelinked));
                    }
                })
                .finally(() => {
                    void toggleSpinner(false);
                })
                .catch((error) => {
                    logWarn(error);
                });
        }
    };
};

export const showPopupForCorrectAddress = (popupRef: PopupRef | undefined, toggleChat: (value: boolean) => void, messages: Messages): void => {
    popupRef?.current?.open(
        <SimplePopup
            popupRef={popupRef}
            text={
                <>
                    <p>{messages.receiveAccountTextPopup1}</p>
                    <p>{messages.receiveAccountTextPopup2}</p>
                </>
            }
        >
            <PopupButton
                iconName="message-bubble"
                text={messages.morePopupChatTitle}
                onClick={() => {
                    amplitudeInstance.getInstance().logEvent(GET_CHAT_CLICKED);
                    popupRef?.current?.close();
                    toggleChat(true);
                }}
            />
        </SimplePopup>,
    );
};
